// @flow
import _ from 'lodash';
import { compare, validate } from 'compare-versions';
import {
  MAP_ELEMENT_LABEL,
  BUTTON_TYPE_VALUE,
  ELEMENTS,
  PAGINATION_TYPE,
  MIN_FONT_SIZE,
  MAX_FONT_SIZE,
  MAX_FONT_SIZE_PAGINATION,
  TOOL_BAR_LAYOUT_OPTIONS,
  MAX_FONT_SIZE_COLLECTION_TITLE
} from 'src/constants/theme-settings';
import { MENU_ID } from 'src/constants/theme-settings.jsx';
import { isRatioV2Bool } from 'src/helpers/form-validation-helper';

/**
 * It takes an array of menu items and returns an array of menu items, where each menu item is either a
 * top-level menu item or a child menu item
 */
export const flattenMenu = menus => {
  const result = [];
  for (let item of menus) {
    result.push(item);
    if (!_.isEmpty(item.subMenu)) {
      for (let subItem of item.subMenu) {
        result.push(subItem);
        if (!_.isEmpty(subItem?.children)) {
          if (Array.isArray(subItem.children)) {
            for (let child of subItem.children) {
              result.push(child);
              (child?.children || []).forEach(areaChild => {
                result.push(areaChild);
              });
            }
          }
        }
      }
    } else {
      if (!_.isEmpty(item?.children)) {
        (item?.children || []).forEach(child => {
          result.push(child);
        });
      }
    }
  }
  return result;
};

/**
 * It takes an array of objects, each object has a property called subMenu which is an array of
 * objects.
 *
 * The function returns an array of objects, each object has a property called subMenu which is an
 * array of objects.
 *
 * The difference is that the returned array has no subMenu property.
 *
 * The subMenu property is flattened into the array.
 * @returns the result of the reduce function.
 */
export const flattenMenuParentLevel = menus => {
  const result = [];
  for (let item of menus) {
    result.push(item);
    if (!_.isEmpty(item.subMenu)) {
      for (let subItem of item.subMenu) {
        result.push(subItem);
        if (subItem.isHasAreaChildren) {
          for (let areaChild of subItem.children) {
            result.push(areaChild);
          }
        }
      }
    }
  }
  return result;
};

/**
 * Build menu on the left panel from redux form data setting
 * @param menu
 * @param setting
 * @param i18n
 * @returns menu
 */
export const buildMenuFromThemeSetting = (menu, setting, i18n) => {
  const result = _.cloneDeep(menu).map(menuItem => {
    // Menu level one
    if (menuItem.isAddable) {
      addChildren(setting, menuItem, i18n);
    }
    // Menu level two
    if (!_.isEmpty(menuItem.subMenu)) {
      menuItem.subMenu.forEach(subItem => {
        if (subItem.isAddable) {
          addChildren(setting, subItem, i18n);
        }
        // Menu level three
        if (subItem.isHasAreaChildren) {
          (subItem?.children || []).forEach(areaChild => {
            if (areaChild.isAddable) {
              addChildren(setting, areaChild, i18n);
            }
          });
        }
      });
    }

    if (menuItem.id === 'collectionHeader_setting') {
      // PFSN-46479 -> fix UI when hide collection header and then refresh page, not see the hidden header
      menuItem.isHidden = setting[MENU_ID.COLLECTION_HEADER]?.isHidden || false;
    }

    return menuItem;
  });

  return result;
};

/**
 * Convert data theme setting into redux form data
 * @param dbSetting
 * @returns redux form data.
 */
export const convertToReduxFormData = dbSetting => {
  const transformedData = {};
  Object.keys(dbSetting).forEach(key => {
    if (!_.isEmpty(dbSetting[key].setting)) {
      transformedData[`${key}_setting`] = dbSetting[key].setting;
    }
    if (!_.isEmpty(dbSetting[key].elements)) {
      dbSetting[key].elements.forEach(elemKey => {
        transformedData[`${key}_${elemKey.name}`] = {
          ...elemKey.setting,
          isHidden: elemKey.isHidden,
          position: elemKey.position
        };
      });
    }
  });
  return transformedData;
};

/**
 * Add children to elements of menu
 * @param setting
 * @param menuItem
 * @param i18n
 * @returns
 */
function addChildren(setting, menuItem, i18n) {
  const [menuId] = menuItem.id.split('_');
  let children = [];
  // Filter group key of menu item without 'setting' suffix
  const childrenSettingKeys = Object.keys(setting).filter(key => {
    return key.includes(menuId) && !key.includes('setting');
  });
  childrenSettingKeys.forEach(key => {
    children.push({
      setting: setting[key],
      value: key
    });
  });
  children = _.orderBy(children, 'setting.position', 'asc');
  children.forEach(child => {
    menuItem.children.push({
      value: child.value,
      label: i18n.translate(MAP_ELEMENT_LABEL[child.value]),
      position: child?.setting?.position,
      isHidden: child?.setting?.isHidden
    });
  });
}

/**
 * Convert redux form data into data theme setting
 * @param formValues redux form
 * @returns redux form data.
 */
export const convertToDataThemeSetting = formValues => {
  const transformedData = {};

  _(formValues)
    .keys()
    .forEach(key => {
      const [prefix, menuId] = String(key).split('_');
      if (!transformedData[prefix]) {
        transformedData[prefix] = {
          setting: {},
          elements: []
        };
      }
      if (menuId === 'setting' || menuId === undefined) {
        transformedData[prefix].setting = formValues[key];
      } else {
        transformedData[prefix].elements.push({
          name: menuId,
          setting: formValues[key]
          // isHidden: formValues[key]?.isHidden || false,
          // position: formValues[key]?.position
        });
      }
    });

  _(transformedData)
    .keys()
    .forEach(key => {
      if (!_.isEmpty(transformedData[key]?.elements)) {
        transformedData[key].elements = _.orderBy(
          transformedData[key]?.elements,
          'setting.position',
          'asc'
        );
      }
    });
  return transformedData;
};

/**
 * Convert data theme setting into shopify meta field setting
 * @param dbSetting data theme setting
 * @returns redux form data.
 */
/* Sample db setting
  productInformation: {
    setting: {},
    elements: []
  },
  productImage: {
    setting: {},
    elements: [
      swatchItem1: {},
      swatchItem2: {}
    ]
  }
*/
export const convertToShopifyThemeSetting = (dbSetting, themeSettingDefaultValue = null) => {
  const transformedData = {
    productItems: {
      general: {},
      productImg: {
        elements: {},
        grid: {}
      }
    },
    additionalElements: {}
  };

  if ('productItem' in dbSetting) {
    transformProductItem(transformedData, dbSetting);
  }

  // Product image
  if ('productImage' in dbSetting) {
    transformProductImage(transformedData, dbSetting);
  }

  if ('productImageTop' in dbSetting) {
    const productImg = transformedData['productItems']['productImg'];
    const productImageTop = _.cloneDeep(dbSetting['productImageTop']);
    const { gridElement, isEmpty } = buildDataGrid(productImg, productImageTop);
    if (!isEmpty) {
      productImg.grid.top = gridElement;
    }
  }

  if ('productImageMiddle' in dbSetting) {
    const productImg = transformedData['productItems']['productImg'];
    const productImageMiddle = _.cloneDeep(dbSetting['productImageMiddle']);
    const { gridElement, isEmpty } = buildDataGrid(productImg, productImageMiddle);
    if (!isEmpty) {
      productImg.grid.middle = gridElement;
    }
  }

  if ('productImageBottom' in dbSetting) {
    const productImg = transformedData['productItems']['productImg'];
    const productImageBottom = _.cloneDeep(dbSetting['productImageBottom']);
    const { gridElement, isEmpty } = buildDataGrid(productImg, productImageBottom);
    if (!isEmpty) {
      productImg.grid.bottom = gridElement;
    }
  }

  if (_.isEmpty(transformedData['productItems']['productImg']['elements'])) {
    delete transformedData['productItems']['productImg']['elements'];
    delete transformedData['productItems']['productImg']['grid'];
  }

  // Product information
  if ('productInformation' in dbSetting) {
    transformProductInformation(transformedData, dbSetting);
  }

  // Quick view
  if ('quickView' in dbSetting) {
    transformQuickView(transformedData, dbSetting);
  }

  // Ajax cart
  if ('cart' in dbSetting) {
    transformCart(transformedData, dbSetting);
  }

  // Product list
  if ('productList' in dbSetting) {
    const productList = dbSetting['productList'];
    delete productList?.setting?.pageLayoutType;
    transformedData['productList'] = productList.setting;
  }

  // Pagination
  if ('pagination' in dbSetting) {
    transformPagination(transformedData, dbSetting, themeSettingDefaultValue);
  }

  // Toolbar
  if ('toolbar' in dbSetting) {
    transformToolbar(transformedData, dbSetting);
  }

  // Collection header
  if ('collectionHeader' in dbSetting) {
    transformCollectionHeader(transformedData, dbSetting);
  }

  return transformedData;
};

function transformProductItem(transformedData, dbSetting) {
  const productItem = _.cloneDeep(dbSetting['productItem']);
  const subLayout = productItem?.setting?.subLayout;

  delete productItem?.setting?.mainLayout;
  delete productItem?.setting?.subLayout;
  transformedData['productItems']['general'] = productItem.setting;

  const layoutHasButtonSpacing = ['subLayout_1_3', 'subLayout_2_8']; // Update for FE fix issue PFSN-44120 when theme setting does not define spacing setting
  if (layoutHasButtonSpacing.includes(subLayout)) {
    transformedData['productItems']['general']['buttonWithSpacing'] = true;
  }
}

function transformProductImage(transformedData, dbSetting) {
  const productImage = _.cloneDeep(dbSetting['productImage']);
  const setting = productImage.setting;
  if (setting?.aspectRatioType === 'natural' || setting?.aspectRatioType === 'auto') {
    delete setting?.cropPosition;
  }
  if (setting?.aspectRatioType !== 'manual') {
    delete setting?.manualAspectRatio;
  } else {
    if (isRatioV2Bool(setting.manualAspectRatio)) {
      setting.manualAspectRatio = setting.manualAspectRatio.replace(':', '/'); // Convert format number:number -> number/number
    } else {
      delete setting?.manualAspectRatio;
    }
  }

  transformedData['productItems']['productImg'] = {
    ...transformedData['productItems']['productImg'],
    ...setting
  };
}

function transformProductInformation(transformedData, dbSetting) {
  const productInformation = _.cloneDeep(dbSetting['productInformation']);
  const dataProductInfo = { ...productInformation.setting };
  if (!_.isEmpty(productInformation?.elements)) {
    dataProductInfo.elements = {};
  }
  transformedData['productItems']['productInfo'] = dataProductInfo;

  const productInfo = transformedData['productItems']['productInfo'];
  (productInformation?.elements || []).forEach(elem => {
    if (elem?.setting?.isHidden) {
      return;
    }
    const elementName = transformElementName(elem);
    const mapElementName = {
      productTitle: 'title',
      productVendor: 'vendor',
      productPrice: 'price',
      rating: 'rating',
      inventoryStatus: 'inventoryStatus'
    };
    const transformedElementName = mapElementName[elementName]
      ? mapElementName[elementName]
      : elementName;

    let setting = { ...elem.setting };
    // Convert fontStyle into fontStyle and fontWeight
    if (
      [
        'productTitle',
        'inventoryStatus',
        'productVendor',
        'qvBtn',
        'selectOptionBtn',
        'goToDetailBtn'
      ].includes(elementName)
    ) {
      if (setting?.fontStyle) {
        const [fontStyle, fontWeight] = splitFontStyle(setting.fontStyle);
        setting.fontWeight = fontWeight;
        setting.fontStyle = fontStyle;
      }

      if (!isValidFontSize(setting?.fontSize)) {
        delete setting?.fontSize;
      }
    }

    if (elementName === 'productPrice') {
      if (setting?.priceFontStyle) {
        const [fontStyle, fontWeight] = splitFontStyle(setting.priceFontStyle);
        setting.priceFontWeight = fontWeight;
        setting.priceFontStyle = fontStyle;
      }
      if (!isValidFontSize(setting?.priceFontSize)) {
        delete setting?.priceFontSize;
      }

      if (setting?.salePriceFontStyle) {
        const [fontStyle, fontWeight] = splitFontStyle(setting.salePriceFontStyle);
        setting.salePriceFontWeight = fontWeight;
        setting.salePriceFontStyle = fontStyle;
      }
      if (!isValidFontSize(setting?.salePriceFontSize)) {
        delete setting?.salePriceFontSize;
      }

      if (setting?.compareAtPriceFontStyle) {
        const [fontStyle, fontWeight] = splitFontStyle(setting.compareAtPriceFontStyle);
        setting.compareAtPriceFontWeight = fontWeight;
        setting.compareAtPriceFontStyle = fontStyle;
      }
      if (!isValidFontSize(setting?.compareAtPriceFontSize)) {
        delete setting?.compareAtPriceFontSize;
      }
      // Delete text field
      delete setting?.savingDisplayText;
    }

    if (elementName === 'inventoryStatus') {
      // Delete text field
      delete setting?.inStockText;
      delete setting?.lowStockText;
      delete setting?.soldOutText;
    }

    // Convert widthUnit into string with Width button
    // Convert button width option into Width button
    if (['qvBtn', 'selectOptionBtn', 'goToDetailBtn'].includes(elementName)) {
      // Split font style
      if (setting?.fontStyle) {
        const [fontStyle, fontWeight] = splitFontStyle(setting?.fontStyle);
        setting.fontStyle = fontStyle;
        setting.fontWeight = fontWeight;
      }

      if (!isValidFontSize(setting?.fontSize)) {
        delete setting?.fontSize;
      }

      transformWidthButtonData(setting);
      // Delete text field
      delete setting?.imgSize;
      delete setting?.imgName;
      delete setting?.quickViewText;
      delete setting?.viewProductText;
      delete setting?.addToCartText;
      delete setting?.selectOptionText;
      delete setting?.soldOutText;
      delete setting?.addingText;
      delete setting?.addedText;
    }

    // Product rating
    if (elementName === 'rating') {
      setting = true;
    }

    // Remove redundant field
    delete setting?.isHidden;
    delete setting?.position;
    productInfo.elements[transformedElementName] = setting;
  });
}

function transformQuickView(transformedData, dbSetting) {
  const quickView = _.cloneDeep(dbSetting['quickView']);
  transformedData['quickView'] = {};

  transformedData['quickView']['showProductImage'] = quickView?.setting?.showProductImage || false;
  transformedData['quickView']['thumbnailPosition'] = quickView?.setting?.thumbnailPosition;

  // Button overall
  if (quickView?.setting?.buttonOverall) {
    // Split font style
    if (quickView?.setting?.buttonOverall?.fontStyle) {
      const [fontStyle, fontWeight] = splitFontStyle(
        quickView?.setting?.buttonOverall?.fontStyle || ''
      );
      quickView.setting.buttonOverall.fontStyle = fontStyle;
      quickView.setting.buttonOverall.fontWeight = fontWeight;
    }
    if (!isValidFontSize(quickView?.setting?.fontSize)) {
      delete quickView?.setting?.fontSize;
    }
    transformedData['quickView']['buttonOverall'] = quickView?.setting?.buttonOverall || {};
  }

  // Buy it now button
  if (!_.isEmpty(quickView?.setting?.buyItNowBtn)) {
    delete quickView?.setting?.buyItNowBtn?.text;
    transformedData['quickView']['buyItNowBtn'] = quickView?.setting?.buyItNowBtn;
  }

  // Add to cart button
  if (!_.isEmpty(quickView?.setting?.addToCartBtn)) {
    transformedData['quickView']['addToCartBtn'] = quickView?.setting?.addToCartBtn;
  }
}

function transformCart(transformedData, dbSetting) {
  const cart = _.cloneDeep(dbSetting['cart']);
  transformedData['cart'] = {};
  transformedData['cart']['enableCart'] = cart?.setting?.enableCart || false;
  transformedData['cart']['cartStyle'] = cart?.setting?.cartStyle;

  if (cart?.setting?.generalLayout) {
    // Split font style
    if (cart?.setting?.generalLayout?.fontStyle) {
      const [fontStyle, fontWeight] = splitFontStyle(cart?.setting?.generalLayout?.fontStyle || '');
      cart.setting.generalLayout.fontStyle = fontStyle;
      cart.setting.generalLayout.fontWeight = fontWeight;
    }
    if (!isValidFontSize(cart?.setting?.generalLayout?.fontSize)) {
      delete cart?.setting?.generalLayout?.fontSize;
    }
    transformedData['cart']['generalLayout'] = cart?.setting?.generalLayout;
  }

  delete cart?.setting?.checkoutBtn?.text;
  transformedData['cart']['checkoutBtn'] = cart?.setting?.checkoutBtn || {};

  delete cart?.setting?.viewCartBtn?.text;
  transformedData['cart']['viewCartBtn'] = cart?.setting?.viewCartBtn || {};
}

function transformPagination(transformedData, dbSetting, themeSettingDefaultValue) {
  const pagination = _.cloneDeep(dbSetting['pagination']);
  //Remove text
  delete pagination?.setting?.button?.previousText;
  delete pagination?.setting?.button?.nextText;
  delete pagination?.setting?.button?.loadMoreText;

  // Convert fontStyle-fontWeight -> fontStyle fontWeight
  if (pagination?.setting?.number?.fontStyle) {
    const [fontStyle, fontWeight] = splitFontStyle(pagination?.setting?.number?.fontStyle);
    pagination.setting.number.fontStyle = fontStyle;
    pagination.setting.number.fontWeight = fontWeight;
  }
  if (
    !isValidFontSize(pagination?.setting?.number?.fontSize, MIN_FONT_SIZE, MAX_FONT_SIZE_PAGINATION)
  ) {
    delete pagination?.setting?.number?.fontSize;
  }

  if (pagination?.setting?.button?.fontStyle) {
    const [fontStyle, fontWeight] = splitFontStyle(pagination?.setting?.button?.fontStyle);
    pagination.setting.button.fontStyle = fontStyle;
    pagination.setting.button.fontWeight = fontWeight;
  }
  if (
    !isValidFontSize(pagination?.setting?.button?.fontSize, MIN_FONT_SIZE, MAX_FONT_SIZE_PAGINATION)
  ) {
    delete pagination?.setting?.button?.fontSize;
  }

  if (
    // Clone setting from button load more info infinite scroll
    pagination?.setting?.paginationType === 'infinite_scroll' &&
    !_.isEmpty(themeSettingDefaultValue)
  ) {
    _.set(
      pagination,
      'setting.button',
      _.get(themeSettingDefaultValue, 'value.pagination_load_more.button')
    );
    delete pagination?.setting?.button?.loadMoreText;
  }

  transformedData['additionalElements']['pagination'] = pagination?.setting;
}

function transformToolbar(transformedData, dbSetting) {
  const toolbar = _.cloneDeep(dbSetting['toolbar']);
  transformedData['additionalElements']['toolbar'] = {};

  transformedData['additionalElements']['toolbar'] = toolbar?.setting;
  if (!_.isEmpty(toolbar?.elements)) {
    transformedData['additionalElements']['toolbar']['elements'] = {};
  }

  (toolbar?.elements || []).forEach(elem => {
    if (elem?.setting?.isHidden) {
      return;
    }
    const { name } = elem;

    let setting = { ...elem.setting };
    // Remove redundant field
    delete setting?.isHidden;
    delete setting?.position;
    transformedData['additionalElements']['toolbar']['elements'][name] = setting;
  });
}

function transformCollectionHeader(transformedData, dbSetting) {
  const collectionHeader = _.cloneDeep(dbSetting['collectionHeader']);
  // Build object when collection header is NOT hidden
  if (!collectionHeader?.setting?.isHidden) {
    transformedData['additionalElements']['collectionHeader'] = {};
    transformedData['additionalElements']['collectionHeader'] = collectionHeader?.setting;

    if (!_.isEmpty(collectionHeader?.elements)) {
      transformedData['additionalElements']['collectionHeader']['elements'] = {};
    }

    (collectionHeader?.elements || []).forEach(elem => {
      if (elem?.setting?.isHidden) {
        return;
      }
      const { name } = elem;

      let setting = { ...elem.setting };

      // Convert fontStyle-fontWeight -> fontStyle fontWeight
      if (name === 'collectionTitle' && setting?.fontStyle) {
        const [fontStyle, fontWeight] = splitFontStyle(setting?.fontStyle);
        setting.fontStyle = fontStyle;
        setting.fontWeight = fontWeight;
      }
      if (!isValidFontSize(setting?.fontSize, MIN_FONT_SIZE, MAX_FONT_SIZE_COLLECTION_TITLE)) {
        delete setting?.fontSize;
      }

      // Remove redundant field
      delete setting?.isHidden;
      delete setting?.position;
      transformedData['additionalElements']['collectionHeader']['elements'][name] = setting;
    });
  }
}

function transformElementName(element) {
  let { name: elementName, setting } = element;
  if (elementName.includes('swatches')) {
    elementName = elementName.replace('-', '.');
  }

  if (['button1', 'button2'].includes(elementName) && setting?.buttonType) {
    elementName = setting?.buttonType;
  }
  return elementName;
}

/**
 * Build data grid and set element setting
 * @param productImg product image object
 * @param productImgArea product image area
 * @returns redux form data.
 */
function buildDataGrid(productImg, productImgArea) {
  const mapProductImageElementName = {
    productSaleLabel: 'saleLabel',
    productSoldOutLabel: 'soldOutLabel',
    productCustomLabelByTag: 'customLabelByTag',
    rating: 'rating'
  };

  const gridElement = {
    direction: productImgArea?.setting?.arrangement,
    elements: {}
  };
  const left = [];
  const center = [];
  const right = [];

  (productImgArea.elements || []).forEach(elem => {
    // Skip hidden element
    if (elem?.setting?.isHidden) {
      return;
    }

    const elementName = transformElementName(elem);
    let { alignment, ...setting } = elem.setting;

    // Button elements
    if (['qvBtn', 'selectOptionBtn', 'goToDetailBtn'].includes(elementName)) {
      // Split font style
      if (setting?.fontStyle) {
        const [fontStyle, fontWeight] = splitFontStyle(setting?.fontStyle);
        setting.fontStyle = fontStyle;
        setting.fontWeight = fontWeight;
      }

      if (!isValidFontSize(setting?.fontSize)) {
        delete setting?.fontSize;
      }

      transformWidthButtonData(setting);
      // Delete text field
      delete setting?.imgSize;
      delete setting?.imgName;
      delete setting?.quickViewText;
      delete setting?.viewProductText;
      delete setting?.addToCartText;
      delete setting?.selectOptionText;
      delete setting?.soldOutText;
      delete setting?.addingText;
      delete setting?.addedText;
    }

    // Product label elements
    if (['productSaleLabel', 'productSoldOutLabel'].includes(elementName)) {
      delete setting?.text;
      delete setting?.imgSize;
      delete setting?.imgName;
    }

    // Product rating
    if (elementName === 'rating') {
      setting = true;
    }

    // Remove field alignment
    delete setting?.alignment;

    productImg.elements[elementName] = setting;

    const gridElementName = mapProductImageElementName[elementName]
      ? mapProductImageElementName[elementName]
      : elementName;

    switch (alignment) {
      case 'left':
        left.push(gridElementName);
        break;
      case 'center':
        center.push(gridElementName);
        break;
      case 'right':
        right.push(gridElementName);
        break;
      default:
        break;
    }
  });
  if (!_.isEmpty(left)) {
    gridElement.elements.left = left;
  }
  if (!_.isEmpty(center)) {
    gridElement.elements.center = center;
  }
  if (!_.isEmpty(right)) {
    gridElement.elements.right = right;
  }

  return { gridElement, isEmpty: _.isEmpty(left) && _.isEmpty(center) && _.isEmpty(right) };
}

function transformWidthButtonData(setting) {
  switch (setting?.buttonWidth) {
    case 'manual-custom': {
      if (setting?.width && setting?.widthUnit) {
        setting.width = `${setting.width}${setting?.widthUnit}`;
      }
      break;
    }
    case '100%': {
      setting.width = '100%';
      delete setting?.height; // Delete when buttonWidth is different from manual-custom
      break;
    }
    case '50%': {
      setting.width = '50%';
      delete setting?.height; // Delete when buttonWidth is different from manual-custom
      break;
    }
    default:
      // Delete when buttonWidth is Auto
      delete setting?.width;
      delete setting?.height;
      break;
  }
  delete setting?.widthUnit;
  delete setting?.buttonWidth;
}

/**
 * Split font style format {fontStyle-fontWeight} separately
 * @param fontStyle
 * @returns [fontStyle, fontWeight]
 */
function splitFontStyle(fontStyleCombine) {
  const [fontStyle, fontWeight] = fontStyleCombine.split('-');
  return [fontStyle, +fontWeight];
}

function isValidFontSize(fontSize, min = MIN_FONT_SIZE, max = MAX_FONT_SIZE) {
  return min <= +fontSize && max >= +fontSize;
}

/**
 * Update translation preview for FE lib v3 render
 * @param themeSetting
 * @param labelTranslation
 * @param metaFieldTranslationSetting
 * @returns translationSetting (FE schema)
 */
export function updateTranslationPreview(
  themeSetting,
  labelTranslation,
  metaFieldTranslationSetting,
  i18n
) {
  const translationSetting = _.cloneDeep(metaFieldTranslationSetting);

  // Button
  const selectOptionBtnKey = Object.keys(themeSetting).find(key => {
    return (
      key.includes('button') &&
      themeSetting[key].buttonType === BUTTON_TYPE_VALUE.SELECTION_ADD_TO_CART
    );
  });
  const selectOptionBtn = themeSetting[selectOptionBtnKey];
  if (selectOptionBtn) {
    _.set(
      translationSetting,
      'productItem.atcAvailableLabel',
      selectOptionBtn.addToCartText ||
        labelTranslation?.atcAvailableLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_AddToCart_Filled')
    );

    _.set(
      translationSetting,
      'productItem.atcSelectOptionsLabel',
      selectOptionBtn.selectOptionText ||
        labelTranslation?.atcSelectOptionsLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_SelectionOption_Filled')
    );

    _.set(
      translationSetting,
      'productItem.atcAddingToCartBtnLabel',
      selectOptionBtn.addingText ||
        labelTranslation?.atcAddingToCartBtnLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_AddingText_Filled')
    );

    _.set(
      translationSetting,
      'productItem.atcAddedToCartBtnLabel',
      selectOptionBtn.addedText ||
        labelTranslation?.atcAddedToCartBtnLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_AddedText_Filled')
    );

    _.set(
      translationSetting,
      'productItem.soldoutLabel',
      selectOptionBtn.soldOutText ||
        labelTranslation?.atcSoldOutLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_SoldOut_Filled')
    );
  }

  const qvBtnKey = Object.keys(themeSetting).find(key => {
    return key.includes('button') && themeSetting[key].buttonType === BUTTON_TYPE_VALUE.QUICK_VIEW;
  });
  const qvBtn = themeSetting[qvBtnKey];
  if (qvBtn) {
    _.set(
      translationSetting,
      'productItem.qvBtnLabel',
      qvBtn.quickViewText ||
        labelTranslation?.qvBtnLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_QuickView_Filled')
    );
  }

  const goToDetailBtnKey = Object.keys(themeSetting).find(key => {
    return (
      key.includes('button') && themeSetting[key].buttonType === BUTTON_TYPE_VALUE.VIEW_PRODUCT
    );
  });
  const goToDetailBtn = themeSetting[goToDetailBtnKey];
  if (goToDetailBtn) {
    _.set(
      translationSetting,
      'productItem.viewProductBtnLabel',
      goToDetailBtn.viewProductText ||
        labelTranslation?.viewProductBtnLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_ViewProduct_Filled')
    );
  }

  // Product price
  const productPrice = themeSetting[ELEMENTS.PRODUCT_INFORMATION_PRODUCT_PRICE];
  if (productPrice) {
    const multiVariantPrice =
      productPrice?.showMultiVariantPrice === 'none' ? '' : productPrice?.showMultiVariantPrice;
    if (multiVariantPrice) {
      _.set(
        translationSetting,
        'productItem.amount',
        multiVariantPrice || labelTranslation?.amount?.value
      );
    }

    _.set(
      translationSetting,
      'productItem.savingAmount',
      productPrice.savingDisplayText ||
        labelTranslation?.savingAmountV2?.value ||
        `${i18n.translate(
          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Filled1'
        )} {{${i18n.translate(
          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Filled2'
        )}}}`
    );
  }

  // Inventory status
  const inventoryStatus = themeSetting[ELEMENTS.PRODUCT_INFORMATION_INVENTORY_STATUS];
  if (inventoryStatus) {
    _.set(
      translationSetting,
      'productItem.inventoryInStock',
      inventoryStatus.inStockText ||
        labelTranslation?.inventoryInStockV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_InStockColor_Filled')
    );

    _.set(
      translationSetting,
      'productItem.inventoryLowStock',
      inventoryStatus.lowStockText ||
        labelTranslation?.inventoryLowStockV2?.value ||
        `${i18n.translate(
          'AdminHTML.ThemeSetting.Form.inventoryStatus_LowStockColor_Filled_1'
        )} {{${i18n.translate(
          'AdminHTML.ThemeSetting.Form.inventoryStatus_LowStockColor_Filled_2'
        )}}} ${i18n.translate(
          'AdminHTML.ThemeSetting.Form.inventoryStatus_LowStockColor_Filled_3'
        )}`
    );

    _.set(
      translationSetting,
      'productItem.inventorySoldOut',
      inventoryStatus.soldOutText ||
        labelTranslation?.inventorySoldOutV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_SoldOutColor_Filled')
    );
  }

  // Swatch
  const [, nameSwatch1] = ELEMENTS.PRODUCT_INFORMATION_SWATCH_ITEM_1.split('_');
  const swatch1Key = Object.keys(themeSetting).find(key => {
    return key.includes(nameSwatch1);
  });
  const swatch1 = themeSetting[swatch1Key];
  if (swatch1) {
    _.set(
      translationSetting,
      'productItem.swatchButtonText1',
      swatch1.swatchButtonStyle || labelTranslation?.swatchButtonText1V2?.value
    );
  }

  const [, nameSwatch2] = ELEMENTS.PRODUCT_INFORMATION_SWATCH_ITEM_2.split('_');
  const swatch2Key = Object.keys(themeSetting).find(key => {
    return key.includes(nameSwatch2);
  });
  const swatch2 = themeSetting[swatch2Key];
  if (swatch2) {
    _.set(
      translationSetting,
      'productItem.swatchButtonText2',
      swatch2.swatchButtonStyle || labelTranslation?.swatchButtonText2V2?.value
    );
  }

  const [, nameSwatch3] = ELEMENTS.PRODUCT_INFORMATION_SWATCH_ITEM_3.split('_');
  const swatch3Key = Object.keys(themeSetting).find(key => {
    return key.includes(nameSwatch3);
  });
  const swatch3 = themeSetting[swatch3Key];
  if (swatch3) {
    _.set(
      translationSetting,
      'productItem.swatchButtonText3',
      swatch3.swatchButtonStyle || labelTranslation?.swatchButtonText3V2?.value
    );
  }

  // Sale label
  const [, nameSaleLabel] = ELEMENTS.PRODUCT_IMAGE_SALE_LABEL.split('_');
  const saleLabelKey = Object.keys(themeSetting).find(key => {
    return key.includes(nameSaleLabel);
  });
  const saleLabel = themeSetting[saleLabelKey];
  if (saleLabel) {
    _.set(
      translationSetting,
      'productItem.productItemSale',
      saleLabel.text ||
        labelTranslation?.productItemSaleV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.saleLabel_Text_Filled')
    );
  }

  // Sold out label
  const [, nameSoldOutLabel] = ELEMENTS.PRODUCT_IMAGE_SOLD_OUT_LABEL.split('_');
  const soldOutLabelKey = Object.keys(themeSetting).find(key => {
    return key.includes(nameSoldOutLabel);
  });
  const soldOutLabel = themeSetting[soldOutLabelKey];
  if (soldOutLabel) {
    _.set(
      translationSetting,
      'productItem.productItemSoldOut',
      soldOutLabel.text ||
        labelTranslation?.productItemSoldOutV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.soldOutLabel_Text_Filled')
    );
  }

  // Quick view
  const quickView = themeSetting[MENU_ID.QUICK_VIEW];
  const buyItNowBtn = quickView?.buyItNowBtn;
  if (buyItNowBtn) {
    _.set(
      translationSetting,
      'quickView.buyItNowBtnLabel',
      buyItNowBtn.text ||
        labelTranslation?.buyItNowBtnLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.quickView_BuyNow_ButtonText_Placeholder')
    );
  }

  // Ajax cart
  const viewCartBtn = themeSetting[MENU_ID.AJAX_CART]?.viewCartBtn;
  if (viewCartBtn) {
    _.set(
      translationSetting,
      'quickView.atcMiniCartViewCartLabel',
      viewCartBtn.text ||
        labelTranslation?.atcMiniCartViewCartLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.ajaxCart_ViewCartButton_ButtonText_Placeholder')
    );
  }

  const checkoutBtn = themeSetting[MENU_ID.AJAX_CART]?.checkoutBtn;
  if (checkoutBtn) {
    _.set(
      translationSetting,
      'quickView.atcMiniCartCheckoutLabel',
      checkoutBtn.text ||
        labelTranslation?.atcMiniCartCheckoutLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.ajaxCart_CheckoutButton_ButtonText_Placeholder')
    );
  }

  // Toolbar
  const productCountToolbar = themeSetting[ELEMENTS.TOOLBAR_PRODUCT_COUNT];
  if (productCountToolbar) {
    _.set(
      translationSetting,
      'productCount.textDescriptionToolbar',
      productCountToolbar.textDescription || labelTranslation?.textDescriptionToolbarV2?.value
    );
    _.set(
      translationSetting,
      'productCount.textDescriptionToolbarPlural',
      productCountToolbar.textDescription || labelTranslation?.textDescriptionToolbarV2?.value
    );
  }

  // Collection header
  const productCountHeader = themeSetting[ELEMENTS.COLLECTION_HEADER_PRODUCT_COUNT];
  if (productCountHeader) {
    _.set(
      translationSetting,
      'productCount.textDescriptionCollectionHeader',
      productCountHeader.textDescription ||
        labelTranslation?.textDescriptionCollectionHeaderV2?.value
    );
    _.set(
      translationSetting,
      'productCount.textDescriptionCollectionHeaderPlural',
      productCountHeader.textDescription ||
        labelTranslation?.textDescriptionCollectionHeaderV2?.value
    );
  }

  // Pagination
  const pagination = themeSetting[MENU_ID.PAGINATION];
  if (pagination) {
    _.set(
      translationSetting,
      'productCount.textDescriptionPagination',
      pagination?.textDescription || labelTranslation?.textDescriptionPaginationV2?.value
    );
    _.set(
      translationSetting,
      'productCount.textDescriptionPaginationPlural',
      pagination?.textDescription || labelTranslation?.textDescriptionPaginationV2?.value
    );

    _.set(
      translationSetting,
      'pagination.loadMoreText',
      pagination?.button?.loadMoreText ||
        labelTranslation?.loadMoreTextV2?.value ||
        i18n.translate(
          'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_LoadMoreText_Placeholder'
        )
    );

    _.set(
      translationSetting,
      'pagination.prevText',
      pagination?.button?.previousText ||
        labelTranslation?.prevTextV2?.value ||
        i18n.translate(
          'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_PreviousText_Placeholder'
        )
    );

    _.set(
      translationSetting,
      'pagination.nextText',
      pagination?.button?.nextText ||
        labelTranslation?.nextTextV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_NextText_Placeholder')
    );
  }

  return translationSetting;
}

/**
 * Update translation preview for FE lib v3 render
 * @param themeSetting
 * @param labelTranslation
 * @returns {boolean} flagEnsure if there is ensure at least one field text
 */
export function ensureDataTranslation(themeSetting, labelTranslation, i18n) {
  // Button
  let flagEnsure = false;
  const selectOptionBtnKey = Object.keys(themeSetting).find(key => {
    return (
      key.includes('button') &&
      themeSetting[key].buttonType === BUTTON_TYPE_VALUE.SELECTION_ADD_TO_CART
    );
  });

  const selectOptionBtn = themeSetting[selectOptionBtnKey];
  if (selectOptionBtn) {
    if (!selectOptionBtn?.addToCartText) {
      _.set(
        selectOptionBtn,
        'addToCartText',
        labelTranslation?.atcAvailableLabelV2?.value ||
          i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_AddToCart_Filled')
      );
      flagEnsure = true;
    }

    if (!selectOptionBtn?.selectOptionText) {
      _.set(
        selectOptionBtn,
        'selectOptionText',
        labelTranslation?.atcSelectOptionsLabelV2?.value ||
          i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_SelectionOption_Filled')
      );
      flagEnsure = true;
    }

    if (!selectOptionBtn?.addingText) {
      _.set(
        selectOptionBtn,
        'addingText',
        labelTranslation?.atcAddingToCartBtnLabelV2?.value ||
          i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_AddingText_Filled')
      );
      flagEnsure = true;
    }

    if (!selectOptionBtn?.addedText) {
      _.set(
        selectOptionBtn,
        'addedText',
        labelTranslation?.atcAddedToCartBtnLabelV2?.value ||
          i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_AddedText_Filled')
      );
      flagEnsure = true;
    }
    if (!selectOptionBtn?.soldOutText) {
      _.set(
        selectOptionBtn,
        'soldOutText',
        labelTranslation?.atcSoldOutLabelV2?.value ||
          i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_SoldOut_Filled')
      );
      flagEnsure = true;
    }
  }

  const qvBtnKey = Object.keys(themeSetting).find(key => {
    return key.includes('button') && themeSetting[key].buttonType === BUTTON_TYPE_VALUE.QUICK_VIEW;
  });
  const qvBtn = themeSetting[qvBtnKey];
  if (qvBtn && !qvBtn.quickViewText) {
    _.set(
      qvBtn,
      'quickViewText',
      labelTranslation?.qvBtnLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_QuickView_Filled')
    );
    flagEnsure = true;
  }

  const goToDetailBtnKey = Object.keys(themeSetting).find(key => {
    return (
      key.includes('button') && themeSetting[key].buttonType === BUTTON_TYPE_VALUE.VIEW_PRODUCT
    );
  });
  const goToDetailBtn = themeSetting[goToDetailBtnKey];
  if (goToDetailBtn && !goToDetailBtn.viewProductText) {
    _.set(
      goToDetailBtn,
      'viewProductText',
      labelTranslation?.viewProductBtnLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_ViewProduct_Filled')
    );
    flagEnsure = true;
  }

  // Product price
  const productPrice = themeSetting[ELEMENTS.PRODUCT_INFORMATION_PRODUCT_PRICE];
  if (productPrice && !productPrice.savingDisplayText) {
    let text = '';
    switch (i18n.locale) {
      case 'en':
        text = `${i18n.translate(
          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Filled1'
        )} {{${i18n.translate(
          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Filled2'
        )}}}`;
        break;
      case 'de':
        text = `{{${i18n.translate(
          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Filled2'
        )}}} ${i18n.translate(
          'AdminHTML.ThemeSetting.Form.productPrice_SavedDisplayText_Filled1'
        )} `;
        break;
      default:
        break;
    }

    _.set(productPrice, 'savingDisplayText', labelTranslation?.savingAmountV2?.value || text);

    flagEnsure = true;
  }

  // Inventory status
  const inventoryStatus = themeSetting[ELEMENTS.PRODUCT_INFORMATION_INVENTORY_STATUS];
  if (inventoryStatus) {
    if (!inventoryStatus?.inStockText) {
      _.set(
        inventoryStatus,
        'inStockText',
        labelTranslation?.inventoryInStockV2?.value ||
          i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_InStockColor_Filled')
      );
      flagEnsure = true;
    }
    if (!inventoryStatus?.lowStockText) {
      _.set(
        inventoryStatus,
        'lowStockText',
        labelTranslation?.inventoryLowStockV2?.value ||
          `${i18n.translate(
            'AdminHTML.ThemeSetting.Form.inventoryStatus_LowStockColor_Filled_1'
          )} {{${i18n.translate(
            'AdminHTML.ThemeSetting.Form.inventoryStatus_LowStockColor_Filled_2'
          )}}} ${i18n.translate(
            'AdminHTML.ThemeSetting.Form.inventoryStatus_LowStockColor_Filled_3'
          )}`
      );
      flagEnsure = true;
    }
    if (!inventoryStatus?.soldOutText) {
      _.set(
        inventoryStatus,
        'soldOutText',
        labelTranslation?.inventorySoldOutV2?.value ||
          i18n.translate('AdminHTML.ThemeSetting.Form.inventoryStatus_SoldOutColor_Filled')
      );
      flagEnsure = true;
    }
  }

  // Sale label
  const [, nameSaleLabel] = ELEMENTS.PRODUCT_IMAGE_SALE_LABEL.split('_');
  const saleLabelKey = Object.keys(themeSetting).find(key => {
    return key.includes(nameSaleLabel);
  });
  const saleLabel = themeSetting[saleLabelKey];
  if (saleLabel && !saleLabel.text) {
    _.set(
      saleLabel,
      'text',
      labelTranslation?.productItemSaleV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.saleLabel_Text_Filled')
    );
    flagEnsure = true;
  }

  // Sold out label
  const [, nameSoldOutLabel] = ELEMENTS.PRODUCT_IMAGE_SOLD_OUT_LABEL.split('_');
  const soldOutLabelKey = Object.keys(themeSetting).find(key => {
    return key.includes(nameSoldOutLabel);
  });
  const soldOutLabel = themeSetting[soldOutLabelKey];
  if (soldOutLabel && !soldOutLabel.text) {
    _.set(
      soldOutLabel,
      'text',
      labelTranslation?.productItemSoldOutV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.soldOutLabel_Text_Filled')
    );
    flagEnsure = true;
  }

  // Quick view
  const quickView = themeSetting[MENU_ID.QUICK_VIEW];
  const buyItNowBtn = quickView?.buyItNowBtn;
  if (buyItNowBtn && !buyItNowBtn.text) {
    _.set(
      buyItNowBtn,
      'text',
      labelTranslation?.buyItNowBtnLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.quickView_BuyNow_ButtonText_Placeholder')
    );
    flagEnsure = true;
  }

  // Ajax cart
  const viewCartBtn = themeSetting[MENU_ID.AJAX_CART]?.viewCartBtn;
  if (viewCartBtn && !viewCartBtn.text) {
    _.set(
      viewCartBtn,
      'text',
      labelTranslation?.atcMiniCartViewCartLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.ajaxCart_ViewCartButton_ButtonText_Placeholder')
    );
    flagEnsure = true;
  }

  const checkoutBtn = themeSetting[MENU_ID.AJAX_CART]?.checkoutBtn;
  if (checkoutBtn && !checkoutBtn.text) {
    _.set(
      checkoutBtn,
      'text',
      labelTranslation?.atcMiniCartCheckoutLabelV2?.value ||
        i18n.translate('AdminHTML.ThemeSetting.Form.ajaxCart_CheckoutButton_ButtonText_Placeholder')
    );
    flagEnsure = true;
  }

  // Pagination
  const pagination = themeSetting[MENU_ID.PAGINATION];
  if (pagination) {
    if (
      !pagination?.button?.loadMoreText &&
      pagination?.paginationType !== PAGINATION_TYPE.INFINITE_SCROLL
    ) {
      _.set(
        pagination,
        'button.loadMoreText',
        labelTranslation?.loadMoreTextV2?.value ||
          i18n.translate(
            'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_LoadMoreText_Placeholder'
          )
      );
      flagEnsure = true;
    }
    if (
      !pagination?.button?.previousText &&
      pagination?.paginationType !== PAGINATION_TYPE.INFINITE_SCROLL
    ) {
      _.set(
        pagination,
        'button.previousText',
        labelTranslation?.prevTextV2?.value ||
          i18n.translate(
            'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_PreviousText_Placeholder'
          )
      );
      flagEnsure = true;
    }
    if (
      !pagination?.button?.nextText &&
      pagination?.paginationType !== PAGINATION_TYPE.INFINITE_SCROLL
    ) {
      _.set(
        pagination,
        'button.nextText',
        labelTranslation?.nextTextV2?.value ||
          i18n.translate('AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_NextText_Placeholder')
      );
      flagEnsure = true;
    }
  }

  return flagEnsure;
}

/**
 * Get default value for toolbar layout, reflected by number of elements on toolbar
 */
export const handleResizeToolbar = (size: number, updateField: any) => {
  const layoutOptions = (TOOL_BAR_LAYOUT_OPTIONS[size] || []).map(option => option?.value);
  const firstOption = _.head(layoutOptions);

  updateField('toolbar_setting.layout', firstOption ?? null);
};

/**
 * Check if boost version theme lib > 2.0 or is alpha
 * @param {string} version
 * @returns {boolean}
 */
export const checkNewVersionBoostThemeLib = (version: string) => {
  return (
    version === 'staging' ||
    version === 'alpha' ||
    (validate(version) && compare(version, '2.0.0', '>='))
  );
};
